import type * as THREE from 'three';
import type { Appearance, HumanPart } from './model';

/** The faces you can pull for a photo (baked by tools/expressions.mjs, kept subtle) */
export type Expression = 'smile' | 'wink' | 'shock';
export const EXPRESSIONS: [Expression, string, string][] = [['smile', '🙂', 'Smile'], ['wink', '😉', 'Wink'], ['shock', '😮', 'Shocked']];

type Baked = Record<'african' | 'asian' | 'caucasian', Record<Expression, number[]>>;
let baked: Promise<Baked | null> | null = null;
/** (fetched the first time a face is pulled: nobody pays for it who never does) */
const load = () => (baked ??= fetch(`${import.meta.env.BASE_URL}human/expressions.json`).then((r) => (r.ok ? r.json() : null)).catch(() => null).then((d) => { if (!d) baked = null; return d; })); // (not kept if it didn't come: the next face tries again)

/**
 * A face on a skinned body: the expression's vertex moves added onto the body's own rest shape (the skeleton then
 * carries them wherever the head is), the proxies that follow the face (eyebrows, lashes, eyes) moved with the body
 * vertices they're fitted to. Mixed by the character's own african/asian/caucasian blend, as MakeHuman does.
 * null puts the face back.
 */
export function faceSetter(meshes: THREE.SkinnedMesh[], parts: HumanPart[], macros: Appearance['macros']): (e: Expression | null) => Promise<void> {
  const rest = new Map<THREE.SkinnedMesh, Float32Array>();
  let token = 0;
  return async (e) => {
    const mine = ++token;
    const data = e ? await load() : null;
    if (mine !== token) return; // (a newer pick came in while this one loaded)
    // the move of each base vertex this face shifts
    let d: Map<number, [number, number, number]> | null = null;
    if (data && e) {
      d = new Map();
      for (const [race, w] of [['african', macros.african], ['asian', macros.asian], ['caucasian', macros.caucasian]] as const) {
        const f = data[race]?.[e];
        if (!f || w < 1e-3) continue;
        for (let k = 0; k < f.length; k += 4) { const a = d.get(f[k]) ?? [0, 0, 0]; a[0] += f[k + 1] * w; a[1] += f[k + 2] * w; a[2] += f[k + 3] * w; d.set(f[k], a); }
      }
    }
    meshes.forEach((mesh, m) => {
      const part = parts[m];
      if (!part) return;
      const pos = mesh.geometry.getAttribute('position') as THREE.BufferAttribute, P = pos.array as Float32Array;
      let r = rest.get(mesh);
      if (!d) { if (r) { P.set(r); pos.needsUpdate = true; } return; }
      if (!r) rest.set(mesh, (r = P.slice()));
      P.set(r);
      let moved = false;
      for (let i = 0; i < part.renderToSrc.length; i++) {
        const v = part.renderToSrc[i];
        let x = 0, y = 0, z = 0;
        if (part.kind === 'body') { const a = d.get(v); if (!a) continue; [x, y, z] = a; }
        else {
          for (let k = 0; k < 3; k++) { const a = d.get(part.refs![v * 3 + k]); if (!a) continue; const w = part.weights![v * 3 + k]; x += a[0] * w; y += a[1] * w; z += a[2] * w; }
          if (!x && !y && !z) continue;
        }
        P[i * 3] += x; P[i * 3 + 1] += y; P[i * 3 + 2] += z; moved = true;
      }
      if (moved) pos.needsUpdate = true;
    });
  };
}
